import { ReactNode, useEffect, useMemo, useState } from "react";
//
import { useAppDispatch } from "../hooks/redux";
import { useNavigate, useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { SystemType } from "../types/main";

import Loading from "../components/Loading";
import { useMenu } from "../contexts/MenuContext";
import Avatar from "../components/ui/avatar";
import { FlexCol, FlexRow } from "../utils/flexLayouts";
import Sidebar, { getNavigation } from "../components/Sidebar";
import DashboardMobileHeader from "./DashboardMobileHeader.mobile";
import { FoldersProvider } from "../contexts/FolderContext";
import { systemsListName } from "src/api/config";
import { modifiedSystemsList } from "src/modules/systems/selectors";
import { getLocalItem, setLocalItem } from "src/utils/localStorage";
import { FiltersProvider } from "src/contexts/FilterContext";
import { getItems } from "src/modules/spItems/actions";
import { useGetOneSelect, useGetSelect } from "src/hooks/redux-selectors";

export type NavItemType = { name: ReactNode; url: string; icon: JSX.Element };

export type NavItemsType = Array<NavItemType>;

export type NavTypeRecord = Record<string, NavItemsType>;

export const initFolderState = {
  collections: null,
  departments: null,
  sectors: null,
  processes: null,
  types: null,
};

export const initFilterState = {
  collections: [],
  departments: [],
  sectors: [],
  processes: [],
  types: [],
  lines: [],
  critical: [],
  sap: [],
  titleEquipment: [],
  manufacturer: [],
  typeEquipment: [],
  model: [],
  created: [],
  responsibleForRevision: [],
  dateForRevision: { id: 0, title: "", date: { start: null, end: null }, required: false },
  templateOWD: [],
  notificationStatus: [],
  notificationType: [],
  workstations: [],
};

const DashboardLayout = ({ children }: { children: ReactNode }) => {
  const storedSidebarState: boolean | null = getLocalItem("sidebar");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [activeSystem, setActiveSystem] = useState<SystemType | null>(null);
  const [menuSystems, setMenuSystems] = useState<SystemType[]>([]);

  const [sidebarOpen, setSidebarOpen] = useState(storedSidebarState ?? false);
  const { id = "1" } = useParams();

  let { data: systems, status } = useGetSelect(systemsListName);

  const newSystems = useMemo(() => modifiedSystemsList(systems), [systems]);

  function toggleSidebar() {
    setSidebarOpen(!sidebarOpen);
    setLocalItem("sidebar", !sidebarOpen);
  }

  useEffect(() => {
    if (!id) {
      navigate("/");
      return;
    }

    if (id && newSystems && newSystems.length > 0) {
      const list = getLastSystems(newSystems, +id);
      setMenuSystems(list);
    }
  }, [id, newSystems, navigate]);

  useEffect(() => {
    if (systems.length > 0) {
      const item = systems.find((item) => item.ID.toString() === id);
      if (item) {
        const newItem = {
          id: +item.ID,
          sort_id: item.Sort_ID,
          title: item.Title,
          description: item.Description,
          url: item.IconUrl,
          externalLink: item.ExternalLink,
        };

        setActiveSystem(newItem);
      }
    }
  }, [systems, id]);

  useEffect(() => {
    if (status === "IDLE") {
      dispatch(
        getItems({
          listName: systemsListName,
          sortOrder: { name: "Sort_ID", asc: true },
        })
      );
    }
  }, [status, dispatch]);

  if (status !== "SUCCESS") return <Loading />;

  return (
    <FoldersProvider initial={initFolderState}>
      <FiltersProvider initial={initFilterState}>
        <DashboardMobileHeader activeSystem={activeSystem} list={getNavigation(id)} />
        <FlexRow className={twMerge(sidebarOpen ? "md:pl-80" : "md:pl-[76px]", "transition-all relative min-h-screen")}>

          <Sidebar
            sidebarOpen={sidebarOpen}
            toggleSidebar={toggleSidebar}
            activeSystem={activeSystem}
            list={menuSystems}
          />
          <Content content={children} />
        </FlexRow>
      </FiltersProvider>
    </FoldersProvider>
  );
};

export default DashboardLayout;

// DashboardMobileHeader moved to `src/layouts/DashboardMobileHeader.mobile.tsx`


function getLastSystems(systems: SystemType[], systemId: number) {
  const { menuSystems } = systems.reduce(
    (acc, system) => {
      if (system.id !== systemId) {
        acc.menuSystems.push(system);
      }
      return acc;
    },
    {
      menuSystems: [] as SystemType[],
    }
  );

  return menuSystems;
}

function Content({ content }: { content: ReactNode }) {
  return (
    <FlexCol className="content-page px-6 md:px-0 flex-1 h-auto w-full max-md:h-screen max-md:overflow-y-auto max-md:overscroll-contain">
      {content}
    </FlexCol>
  );
}

// mobile menu moved to `src/layouts/MobileMenu.mobile.tsx`
